/**
 * How warm a lead is, and what to do about it.
 *
 * A lead is a person who left their address to download a room they made. The
 * vendor has a few minutes a day for them, so the page ranks them by how much
 * a call would be worth right now: how recently they were here, how many
 * rooms they saved, how much they did, how many products they looked at.
 * Recency dominates — a keen visitor from two months ago is not a hot lead.
 *
 * Pure and deterministic (pass `now`), so the thresholds are tested rather
 * than eyeballed.
 */
import type { Lead } from '../../../../common/hooks/use-leads-api.hook';

export type Temperature = 'hot' | 'warm' | 'cold';

/** Why a lead reads as it does, most telling first — shown in the lead's sheet. */
export type TemperatureReason = 'RECENT' | 'REPEAT_DOWNLOADS' | 'VERY_ACTIVE' | 'MANY_PRODUCTS' | 'GONE_QUIET';

export type NextAction =
  /** Hot and never written to: the one to email today. */
  | 'email_now'
  /** Warm, never written to. */
  | 'follow_up'
  /** Written to, and came back since: answer while they are looking. */
  | 'reply'
  /** Written to recently; give it time. */
  | 'wait'
  /** Cold: leave it to an automation. */
  | 'nurture'
  /** Unsubscribed: nothing to send. */
  | 'none';

export interface LeadReading {
  temperature: Temperature;
  /** 0–100. */
  score: number;
  reasons: TemperatureReason[];
  daysSinceSeen: number;
  action: NextAction;
  contacted: boolean;
  /** Came back after the last email. */
  returnedAfterEmail: boolean;
}

const DAY = 86_400_000;
export const HOT_SCORE = 60;
export const WARM_SCORE = 35;

const daysBetween = (from: string | null | undefined, now: number) => {
  const t = from ? new Date(from).getTime() : NaN;
  return Number.isFinite(t) ? Math.max(0, Math.floor((now - t) / DAY)) : Infinity;
};

export function leadScore(lead: Pick<Lead, 'lastSeenAt' | 'eventCount' | 'downloadCount' | 'products'>, now: Date = new Date()) {
  const days = daysBetween(lead.lastSeenAt, now.getTime());
  const recency = days <= 2 ? 40 : days <= 7 ? 30 : days <= 14 ? 20 : days <= 30 ? 10 : 0;
  const downloads = Math.min(Math.max(lead.downloadCount ?? 0, 0), 4) * 7.5;
  const events = lead.eventCount ?? 0;
  const activity = events >= 30 ? 20 : events >= 10 ? 14 : events >= 3 ? 8 : events > 0 ? 3 : 0;
  const products = lead.products?.length ?? 0;
  const breadth = products >= 5 ? 10 : products >= 2 ? 6 : products >= 1 ? 3 : 0;
  return { score: Math.round(recency + downloads + activity + breadth), days };
}

export function readLead(lead: Lead, now: Date = new Date()): LeadReading {
  const { score, days } = leadScore(lead, now);

  let temperature: Temperature = score >= HOT_SCORE ? 'hot' : score >= WARM_SCORE ? 'warm' : 'cold';
  // However busy they once were, a lead nobody has seen in a month is cold,
  // and one quiet for two weeks is at most warm.
  if (days > 30) temperature = 'cold';
  else if (days > 14 && temperature === 'hot') temperature = 'warm';

  const reasons: TemperatureReason[] = [];
  if (days <= 7) reasons.push('RECENT');
  if ((lead.downloadCount ?? 0) >= 2) reasons.push('REPEAT_DOWNLOADS');
  if ((lead.eventCount ?? 0) >= 10) reasons.push('VERY_ACTIVE');
  if ((lead.products?.length ?? 0) >= 3) reasons.push('MANY_PRODUCTS');
  if (days > 14) reasons.push('GONE_QUIET');

  const contacted = (lead.emailsSent ?? 0) > 0;
  const emailedAt = lead.lastEmailedAt ? new Date(lead.lastEmailedAt).getTime() : NaN;
  const seenAt = lead.lastSeenAt ? new Date(lead.lastSeenAt).getTime() : NaN;
  const returnedAfterEmail = contacted && Number.isFinite(emailedAt) && Number.isFinite(seenAt) && seenAt > emailedAt;

  let action: NextAction;
  if (lead.unsubscribedAt) action = 'none';
  // A return is only worth answering while it is fresh.
  else if (returnedAfterEmail && days <= 14) action = 'reply';
  else if (contacted) action = temperature === 'cold' ? 'nurture' : 'wait';
  else if (temperature === 'hot') action = 'email_now';
  else if (temperature === 'warm') action = 'follow_up';
  else action = 'nurture';

  return { temperature, score, reasons, daysSinceSeen: days, action, contacted, returnedAfterEmail };
}

export interface LeadPipeline {
  total: number;
  hot: number;
  warm: number;
  cold: number;
  /** First seen by this vendor inside the period. */
  newInPeriod: number;
  /** Hot or warm, never emailed — the queue for today. */
  waiting: number;
  /** Came back after an email, recently enough to answer. */
  returned: number;
  /** Opted in to marketing mail and not unsubscribed. */
  consented: number;
}

const inRange = (value: string | null | undefined, startDate: string, endDate: string) => {
  if (!value) return false;
  const day = new Date(value).toISOString().slice(0, 10);
  return day >= startDate && day <= endDate;
};

/** The counts above the list. `startDate`/`endDate` are YYYY-MM-DD, inclusive. */
export function summarizeLeads(
  leads: Lead[],
  readings: Map<string, LeadReading>,
  period: { startDate: string; endDate: string },
): LeadPipeline {
  const out: LeadPipeline = { total: leads.length, hot: 0, warm: 0, cold: 0, newInPeriod: 0, waiting: 0, returned: 0, consented: 0 };
  for (const lead of leads) {
    const r = readings.get(lead._id);
    if (!r) continue;
    out[r.temperature] += 1;
    if (inRange(lead.firstSeenAt, period.startDate, period.endDate)) out.newInPeriod += 1;
    if ((r.action === 'email_now' || r.action === 'follow_up')) out.waiting += 1;
    if (r.action === 'reply') out.returned += 1;
    if (lead.marketingConsent && !lead.unsubscribedAt) out.consented += 1;
  }
  return out;
}

const TEMPERATURE_ORDER: Record<Temperature, number> = { hot: 0, warm: 1, cold: 2 };

/** Hottest first; within a temperature, the higher score, then the most recent. */
export function rankLeads(leads: Lead[], readings: Map<string, LeadReading>): Lead[] {
  return [...leads].sort((a, b) => {
    const ra = readings.get(a._id);
    const rb = readings.get(b._id);
    if (!ra || !rb) return 0;
    return (
      TEMPERATURE_ORDER[ra.temperature] - TEMPERATURE_ORDER[rb.temperature] ||
      rb.score - ra.score ||
      new Date(b.lastSeenAt).getTime() - new Date(a.lastSeenAt).getTime()
    );
  });
}

/** Where a lead came from, as one short word for the table: the campaign, else the channel, else the referring site. */
export function leadSource(lead: Pick<Lead, 'attribution'>): string | null {
  const a = lead.attribution;
  if (!a) return null;
  const pick = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  const referrer = pick(a.referrer);
  let host: string | null = null;
  if (referrer) {
    try {
      host = new URL(referrer.includes('://') ? referrer : `https://${referrer}`).hostname.replace(/^www\./, '');
    } catch {
      host = referrer;
    }
  }
  return pick(a.campaign) || pick(a.source) || host;
}
